export type IntakeRowKind = 'case' | 'lead';

export type IntakeFlagFilter =
  | 'all'
  | 'no_contact_2d'
  | 'lor_unconfirmed'
  | 'lor_not_sent'
  | 'lor_confirmed';

export interface IntakeTrackingRowDto {
  kind: IntakeRowKind;
  rowKey: string;
  ticketId: number | null;
  ticketGuid: string | null;
  caseNumber: string | null;
  leadId: string | null;
  leadSource: 'website_intake' | 'wordpress_form' | 'phone_inquiry' | null;
  userId: number | null;
  userGuid: string | null;
  clientName: string;
  clientPhone: string | null;
  clientEmail: string | null;
  courtId: number | null;
  courtName: string | null;
  statusName: string | null;
  createdAt: string;
  assignedStaffName: string | null;
  assignedStaffManagerId: number | null;
  assignedStaffSource: 'explicit' | 'last_contact' | 'unassigned';
  lastContactAt: string | null;
  lastContactBy: string | null;
  lastContactKind: 'call' | 'note' | 'message' | null;
  lastContactSummary: string | null;
  daysSinceLastContact: number;
  lorSentAt: string | null;
  lorConfirmedAt: string | null;
  lorDaysSinceSent: number | null;
  flaggedNoContact2PlusDays: boolean;
  flaggedLorUnconfirmed: boolean;
  flaggedLorNotSent: boolean;
}

export interface MissedCallsSummaryDto {
  totalMissedInboundThisWeek: number;
  voicemailInboundThisWeek: number;
  noAnswerInboundThisWeek: number;
}

export interface UnknownNumbersSummaryDto {
  totalCallsThisWeek: number;
  distinctCallersThisWeek: number;
  inboundCalls: number;
  outboundCalls: number;
  spokeCalls: number;
  noAnswerCalls: number;
  voicemailCalls: number;
}

export interface IntakeAgentWeeklySummaryDto {
  agentName: string;
  managerId: number | null;
  callsThisWeek: number;
  outboundCallsThisWeek: number;
  inboundCallsThisWeek: number;
  connectedCallsThisWeek: number;
  spokeCallsThisWeek?: number;
  unmatchedCallsThisWeek: number;
  followUpNotesThisWeek: number;
  messagesSentThisWeek: number;
  distinctCasesTouchedThisWeek: number;
  assignedOpenCaseCount: number;
  flaggedNoContactCount: number;
  flaggedLorUnconfirmedCount: number;
  isUnattributedLine?: boolean;
  hasActivityThisWeek?: boolean;
}

export interface IntakeHomeResponseDto {
  rows: IntakeTrackingRowDto[];
  totalOpenCases: number;
  totalLeads: number;
  flaggedNoContactCount: number;
  flaggedLorUnconfirmedCount: number;
  flaggedLorNotSentCount: number;
  lorConfirmedCount: number;
  staffOptions: string[];
  agentWeeklySummaries: IntakeAgentWeeklySummaryDto[];
  unattributedDialpadCallsThisWeek: number;
  unmatchedCallsThisWeek: number;
  missedCallsSummary?: MissedCallsSummaryDto;
  unknownNumbersSummary?: UnknownNumbersSummaryDto;
  missingDataDisclosures: string[];
}

export interface FetchIntakeHomeOptions {
  staff?: string;
  flag?: IntakeFlagFilter;
  search?: string;
  createdDays?: number;
}

export async function fetchIntakeHome(
  options: FetchIntakeHomeOptions = {},
): Promise<IntakeHomeResponseDto> {
  const params = new URLSearchParams();
  if (options.staff && options.staff !== 'all') {
    params.set('staff', options.staff);
  }
  if (options.flag && options.flag !== 'all') {
    params.set('flag', options.flag);
  }
  if (options.search?.trim()) {
    params.set('search', options.search.trim());
  }
  if (typeof options.createdDays === 'number' && options.createdDays > 0) {
    params.set('createdDays', String(options.createdDays));
  }

  const response = await fetch(
    `${process.env.API_HOST || ''}/api/v1/intake-home?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch intake home: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
}
